import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Collapse, Typography } from "@mui/material";
import { type FC, type MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useHandleRequest } from "../../hooks/useHandleRequest";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { usePredictCost } from "../../hooks/usePredictCost";
import { usePrepareNurseShift } from "../../hooks/usePrepareNurseShift";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { useScheduleDayOf } from "../../hooks/useScheduleDayOf";
import { useScheduledShiftLookup } from "../../hooks/useScheduledShiftLookup";
import { useScheduleSaves } from "../../hooks/useScheduleSaves";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import { INK, NEUTRAL, STATUS_BADGE, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { buildCostPrediction } from "../../utils/costPrediction";
import { formatPersonFullName } from "../../utils/formatters";
import { NotificationType, type Nurse, type NurseInShift, type ReplaceShiftRequest, type RequestResponsesStatus, type ShiftRequest } from "../../utils/models";
import { countDailyHours } from "../../utils/statsCalculations";
import { acceptedHoursOf } from "../../utils/utilFunctions";
import { isAgencyNurse, useNurseWarnings, WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import { CustomCalendar } from "../CustomCalendar/CustomCalendar";
import { HeaderButton } from "../headerButton";
import { NurseShiftBadges } from "../NurseShiftBadges/NurseShiftBadges";
import { OvertimeCell } from "../UIToolkit/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "../UIToolkit/Cells/OvertimeCell/otVisibility";
import { useRequestUtils } from "./Provider/useRequestUtils";

type RequestDataProps = {
	request: ShiftRequest;
	requestData: RequestResponsesStatus;
};

const createNurseInShiftFromRequest = (
	nurse: Nurse,
	request: ShiftRequest,
	requestData: RequestResponsesStatus,
	departmentId: string,
	lastNameFirst: boolean,
): NurseInShift => {
	const { start, end } = acceptedHoursOf(request, requestData);
	return {
		id: "",
		nurseId: nurse.id,
		departmentId,
		startTime: start,
		endTime: end,
		isPendingToDelete: false,
		isPending: false,
		role: nurse.role,
		charge: false,
		name: formatPersonFullName(nurse, lastNameFirst),
		type: nurse.employmentType,
		shiftType: request.message.shift_type,
		notes: "",
		trainingStatus: nurse.trainingStatus,
		bonus: request.current_bonus ?? request.bonus ?? 0,
		breaks: [],
	};
};

export const RequestData: FC<RequestDataProps> = ({ requestData, request }) => {
	const { t } = useTranslation();
	const { refreshRequests } = useRequestsContext();
	const navigate = useNavigate();
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { getNurseFormattedName, getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const { addNurseToShift, removeNurseFromSpecificDate } = useScheduleSaves();
	const prepareNurseShift = usePrepareNurseShift();
	const scheduleDayOf = useScheduleDayOf();
	const findScheduledShift = useScheduledShiftLookup();
	// A request's date is its shift's start; its schedule day is the day that start belongs to.
	const requestDay = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });
	const schedule = useScheduleWeeks(requestDay.date);
	const { isLoading } = useHandleNurseChange();
	const lastNameFirst = useLastNameFirst();
	const { replaceRequest, isLoading: isLoadingRequest } = useHandleRequest();
	const { showSnackbar } = useSnackbar();
	const canViewWage = useRevealWage();
	const { approveAddRequest, isPartialShift } = useRequestUtils();
	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	// Predict shift cost from backend using the nurse's ACCEPTED times — for a partial shift these differ
	// from the posted window, so the card cost matches what approving will actually cost (and the approval
	// warning below reuses this same prediction rather than re-fetching a different one).
	const { mutate: predictCost, data: shiftPrediction, status: predictionStatus } = usePredictCost();
	const { start: predictStart, end: predictEnd } = acceptedHoursOf(request, requestData);
	const canPredict = !!requestData.nurse_id && !!predictStart && !!predictEnd;
	useEffect(() => {
		if (!canPredict) return;
		predictCost({
			departmentId: chosenDepartment.department_id,
			nurseId: requestData.nurse_id,
			startTime: predictStart.toISO() ?? "",
			endTime: predictEnd.toISO() ?? "",
		});
	}, [canPredict, requestData.nurse_id, predictStart, predictEnd, chosenDepartment.department_id, predictCost]);
	// The approval warnings are derived from this prediction and the day's other shifts, so approving before both settle
	// would silently skip the OT / daily-hours gates.
	const isPredictionUnsettled = (canPredict && (predictionStatus === "idle" || predictionStatus === "pending")) || schedule.isLoading;
	// Unified cost + OT display derived through the same buildCostPrediction path as every other view.
	const responder = getNurseById(requestData.nurse_id);
	const cost = buildCostPrediction(shiftPrediction, { isGlobalSalary: Boolean(responder?.isGlobalSalary) });

	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings] = useNurseWarnings(
		async () => {
			await executeApproveRequest();
		},
		chosenDepartment.longShiftDuration,
	);

	const [pendingNurseId, setPendingNurseId] = useState<number | null>(null);
	const handleApproveClick = async (e: MouseEvent, nurseId: number): Promise<void> => {
		e.stopPropagation();
		setPendingNurseId(nurseId);

		const nurse = getNurseById(nurseId);
		if (!nurse) return;
		// The daily-hours warning counts the nurse's other shifts that day, so it can't be checked without the day's schedule.
		if (schedule.isError) {
			showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurse_id: nurseId, request_id: request.id });
			return;
		}

		// Reuse the card's prediction (already computed on the nurse's accepted times, for this same nurse),
		// so the warning gates on exactly the cost/OT the admin was just shown. dailyHours needs the
		// weekly-hours deltas from that same prediction; guard so a not-yet-loaded prediction still surfaces
		// the warning safely with zeroed hours.
		let dailyHours = 0;
		if (shiftPrediction) {
			const shiftHours = shiftPrediction.weeklyHoursAfter - shiftPrediction.weeklyHoursBefore;
			// The request's own row finds its week even when another row skips that date.
			const requestSlot = schedule.slotOf(requestDay.date, requestDay.shiftType);
			// DEFAULT: a day with no staffing row has no other shifts of the nurse's to count.
			const originalDailyHours = requestSlot
				? countDailyHours({
						nurseId: nurse.id,
						departmentId: chosenDepartment.department_id,
						weeklyShifts: schedule.weeks[requestSlot.weekIndex].weeklyShifts,
						// The calendar weekday every row is matched on, not one row's own position.
						dayIndex: requestDay.date.weekdayISO,
						breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
						nurseUnion: nurse.union,
					})
				: 0;
			dailyHours = originalDailyHours + shiftHours;
		}

		const isAgency = isAgencyNurse(nurse.employmentType);

		checkWarnings(isAgency, cost.overtimeHours ?? 0, cost.costDisplay, dailyHours);
	};

	const executeApproveRequest = async (): Promise<void> => {
		if (!pendingNurseId) return;
		await approveRequest(pendingNurseId);
		setPendingNurseId(null);
	};

	const approveRequest = async (nurseId: number): Promise<void> => {
		trackEvent("approve_request_mobile", {
			request_type: request.request_type,
			nurse_id: nurseId,
		});

		try {
			switch (request.request_type) {
				case NotificationType.OpenShift:
					await approveAddRequest(nurseId, request);
					break;
				case NotificationType.ReplaceShift:
					await approveReplaceRequest(nurseId);
					break;
				default:
					return;
			}
		} catch {
			return;
		}

		await refreshRequests();
		navigate(`/${chosenDepartment.department_id}/staffing/${request.message.shift_start.toFormat("yyyy-MM-dd")}`);
	};

	const approveReplaceRequest = async (nurseId: number): Promise<void> => {
		const { request_for_shift: requestForShift, replacement_nurse_id: replacementNurseId } = request as ReplaceShiftRequest;
		// Backward compatability support: older requests name the nurse, not the shift.
		let nurseToRemove: NurseInShift | undefined;
		try {
			nurseToRemove = await findScheduledShift(requestForShift ? { ...requestDay, shiftId: requestForShift } : { ...requestDay, nurseId: replacementNurseId });
		} catch (e) {
			showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurse_id: nurseId, request_id: request.id }, e);
			return;
		}

		if (!nurseToRemove) {
			showErrorSnackbar("Failed to approve request - missing nurse", { nurse_id: nurseId, request_id: request.id });
			return;
		}
		// The replacement is added to the request's own slot; without it the local schedule couldn't show the swap.
		if (!schedule.slotOf(requestDay.date, requestDay.shiftType)) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseId, request_id: request.id, reason: "request day not loaded" });
			return;
		}

		const nurse = getNurseById(nurseId);
		const nurseToAdd =
			nurse &&
			prepareNurseShift({
				nurse,
				...requestDay,
				location: nurseToRemove.location,
				addedFromActual: false,
				requestId: request.id,
				shiftTimes: undefined,
				nurseInShiftId: undefined,
				shiftBonus: nurseToRemove.bonus,
				isDraftMode: false,
			});
		if (!nurseToAdd) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseId, request_id: request.id, reason: "nurse not on the department roster" });
			return;
		}
		nurseToAdd.location = nurseToRemove.location;

		try {
			await replaceRequest({
				nurseToAdd,
				nurseToRemove,
				requestId: request?.id,
				date: request.message.date,
			});

			removeNurseFromSpecificDate(requestDay.date, requestDay.shiftType, nurseToRemove.nurseId, false, nurseToRemove.id);
			addNurseToShift(nurseToAdd, requestDay.date, requestDay.shiftType);
			showSnackbar(t("success.request.approved"), "success");
		} catch (e) {
			showErrorSnackbar("Failed to approve request", { nurse_id: nurseToAdd.id, request_id: request?.id }, e);
			throw e;
		}
	};

	return (
		<Box
			data-testid={`request-data-${requestData.nurse_id}`}
			sx={{ backgroundColor: SURFACE, padding: 2, marginBottom: 1 }}
			onClick={() => setIsExpanded(!isExpanded)}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between" }}>
				<Typography sx={{ fontWeight: 600 }}>{getNurseFormattedName(requestData.nurse_id)}</Typography>
				<KeyboardArrowDownIcon
					sx={{
						color: INK.MUTED,
						transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
						transition: "transform 0.3s ease",
						fontSize: 20,
					}}
				/>
			</Box>

			{/* Display accepted times if they exist */}
			{requestData.accepted_start_time && requestData.accepted_end_time && (
				<Box
					sx={{
						display: "flex",
						gap: 1.2,
						marginTop: "3px",
						alignItems: "center",
					}}
				>
					<Typography sx={{ fontSize: "14px", color: STATUS_BADGE.WARN_INK, fontWeight: 500 }}>accepted Times:</Typography>
					<Typography sx={{ fontSize: "14px", color: INK.SECONDARY }}>
						{requestData.accepted_start_time.toFormat("h:mm a")} - {requestData.accepted_end_time.toFormat("h:mm a")}
					</Typography>
				</Box>
			)}

			<Box sx={{ display: "flex", gap: 1.2, marginTop: "3px" }}>
				<Typography>{t("predicted.cost")}</Typography>
				{canViewWage && (
					<Typography sx={{ marginLeft: "3px" }}>
						{(() => {
							const displayBonus = request.current_bonus ?? request.bonus;
							// Marginal weekly delta (unified), replacing the old raw shiftCost. Bonus appended per grid convention.
							return isPartialShift(request, requestData.nurse_id) || !displayBonus ? cost.costDisplay : `${cost.costDisplay} (+$${displayBonus})`;
						})()}
					</Typography>
				)}
				{/* Someone who answered but is no longer on this department's roster has no badges to show. */}
				{responder && (
					<NurseShiftBadges shift={createNurseInShiftFromRequest(responder, request, requestData, chosenDepartment.department_id, lastNameFirst)} />
				)}
			</Box>

			{shouldShowOtColumn({ hasOvertimeData: cost.overtimeHours != null, isMedicalDepartment }) && (
				<Box sx={{ display: "flex", gap: 1.2, marginTop: "3px", alignItems: "baseline" }}>
					<Typography>{t("nurses.matrix.ot")}</Typography>
					<OvertimeCell
						overtimeHours={cost.overtimeHours ?? 0}
						weeklyOvertimeHoursBeforeChange={cost.weeklyOvertimeHoursBeforeChange}
						showWeeklyOt={cost.weeklyOvertimeHoursBeforeChange != null}
						isAdd
						detailed
						t={t}
					/>
				</Box>
			)}
			<Collapse sx={{ marginTop: "10px" }} in={isExpanded}>
				<Box
					sx={{
						bgcolor: SURFACE,
						borderTop: `1px solid ${NEUTRAL[300]}`,
						position: "relative",
						zIndex: 0,
					}}
				>
					<CustomCalendar value={request.message.shift_start.startOf("day")} isOpen={isExpanded} showOneWeek={true} shifts={schedule.viewerShifts} />
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: "10px",
						}}
					>
						<HeaderButton
							data-testid={`request-approve-${request.id}-${requestData.nurse_id}`}
							onPress={(e: MouseEvent) => handleApproveClick(e, requestData.nurse_id)}
							state="on"
							text={t("approve.request")}
							icon={<CheckIcon style={{ fontSize: "20px" }} />}
							// Someone no longer on this department's roster can't be approved into it.
							disabled={!responder || isLoading.addNurse || isLoadingRequest.replaceRequest || isPredictionUnsettled}
							width="150px"
							height="30px"
							secondary={false}
						/>
					</Box>
				</Box>
			</Collapse>

			<WarningDialog
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</Box>
	);
};
